import React, { useState, useEffect } from 'react';
import { Activity, Calendar, Target, TrendingUp, TrendingDown, RefreshCw, Zap, Star, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import API_URL from '../config';

// Configuración de deportes: orden de pestañas MLB, NHL, NBA, NFL, WNBA, NCAAF, NCAAB.
// Cada deporte define su campo de protagonista, etiqueta y las claves de mercado.
const SPORTS = [
  {
    key: 'mlb', label: 'MLB', emoji: '⚾',
    heroField: 'lanzador_principal', heroLabel: 'Abridor',
    market: { line: 'linea_dinero', total: 'total_carreras', totalLabel: 'O/U Carreras', spread: null, spreadLabel: null }
  },
  {
    key: 'nhl', label: 'NHL', emoji: '🏒',
    heroField: 'protagonista_principal', heroLabel: 'Protagonista',
    market: { line: 'linea_principal', total: 'total_puntos', totalLabel: 'O/U Goles', spread: 'spread', spreadLabel: 'Puckline' }
  },
  {
    key: 'nba', label: 'NBA', emoji: '🏀',
    heroField: 'protagonista_principal', heroLabel: 'Protagonista',
    market: { line: 'linea_principal', total: 'total_puntos', totalLabel: 'O/U Puntos', spread: 'spread', spreadLabel: 'Spread' }
  },
  {
    key: 'nfl', label: 'NFL', emoji: '🏈',
    heroField: 'protagonista_principal', heroLabel: 'Protagonista',
    market: { line: 'linea_principal', total: 'total_puntos', totalLabel: 'O/U Puntos', spread: 'spread', spreadLabel: 'Spread' }
  },
  {
    key: 'wnba', label: 'WNBA', emoji: '🏀',
    heroField: 'protagonista_principal', heroLabel: 'Protagonista',
    market: { line: 'linea_principal', total: 'total_puntos', totalLabel: 'O/U Puntos', spread: 'spread', spreadLabel: 'Spread' }
  },
  {
    key: 'ncaaf', label: 'NCAAF', emoji: '🏈',
    heroField: 'protagonista_principal', heroLabel: 'Protagonista',
    market: { line: 'linea_principal', total: 'total_puntos', totalLabel: 'O/U Puntos', spread: 'spread', spreadLabel: 'Spread' }
  },
  {
    key: 'ncaab', label: 'NCAAB', emoji: '🏀',
    heroField: 'protagonista_principal', heroLabel: 'Protagonista',
    market: { line: 'linea_principal', total: 'total_puntos', totalLabel: 'O/U Puntos', spread: 'spread', spreadLabel: 'Spread' }
  }
];

const teamLogos = {
  'ARI': '🔴', 'ATL': '🔴', 'BAL': '🟠', 'BOS': '🔴', 'CHW': '⚪',
  'CIN': '🔴', 'CLE': '🔵', 'COL': '🟣', 'DET': '🔵', 'HOU': '🔴',
  'KC': '🔴', 'LAA': '🔴', 'LAD': '🔵', 'MIA': '🔵', 'MIL': '🔵',
  'MIN': '🔵', 'NYM': '🔵', 'NYY': '⚪', 'ATH': '🟢', 'PHI': '🔴',
  'PIT': '🟡', 'SD': '🟤', 'SEA': '🔵', 'SF': '🟠', 'STL': '🔴',
  'TB': '🔵', 'TEX': '🔵', 'TOR': '🔵', 'WSH': '🔴'
};

export default function SportsAnalysisView() {
  const [activeKey, setActiveKey] = useState(SPORTS[0].key);
  const [analisis, setAnalisis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const activeSport = SPORTS.find(s => s.key === activeKey) || SPORTS[0];

  const fetchAnalisis = async (sport) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/daily-analisis/${sport.key}`);
      setAnalisis(response.data);
    } catch (err) {
      setAnalisis(null);
      setError(err.response?.data?.message || `Error al cargar el análisis ${sport.label}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Resetear el estado al cambiar de pestaña para no mostrar datos de otro deporte.
    setAnalisis(null);
    setError(null);
    const sport = SPORTS.find(s => s.key === activeKey) || SPORTS[0];
    fetchAnalisis(sport);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    // timeZone: 'UTC' evita el corrimiento de un día: las fechas se guardan como
    // fecha pura (p.ej. "2026-08-21"), que JS parsea como medianoche UTC; sin esto,
    // un navegador en huso negativo (ej. America/Argentina UTC-3) la muestra un día antes.
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC'
    });
  };

  const parseProb = (prob) => {
    if (typeof prob === 'number') return prob;
    if (typeof prob === 'string') return parseInt(prob) / 100;
    return 0;
  };

  const getProbabilityColor = (prob) => {
    const pct = parseProb(prob);
    if (pct >= 0.7) return 'text-green-400';
    if (pct >= 0.5) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getProbabilityBar = (prob) => {
    const pct = parseProb(prob);
    const percentage = Math.round(pct * 100);
    return (
      <div className="flex items-center gap-2">
        <div className="w-24 h-2 bg-slate-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-yellow-500 to-green-400 rounded-full"
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className={`font-black text-sm ${getProbabilityColor(prob)}`}>
          {percentage}%
        </span>
      </div>
    );
  };

  // Texto principal de una card de pronóstico: soporta variante nueva y antigua
  const getCardMainText = (data) => data.seleccion || data.pick || null;
  const getCardSubText = (data) => data.protagonista || data.lanzador || data.pitcher || null;
  const getCardBody = (data) => data.argumento || data.justificacion || null;

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-black italic uppercase text-white flex items-center gap-3">
            <Activity className="text-yellow-500" size={32} />
            Análisis Deportes
          </h1>
          <p className="text-slate-400 mt-1">Análisis detallado de partidos por deporte</p>
        </div>

        <button
          onClick={() => fetchAnalisis(activeSport)}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors"
        >
          <RefreshCw size={16} />
          Actualizar
        </button>
      </div>

      {/* ─── TAB BAR DE DEPORTES ─── */}
      <div className="flex flex-wrap gap-2 mb-8 border-b border-slate-800 pb-4">
        {SPORTS.map(sport => (
          <button
            key={sport.key}
            onClick={() => setActiveKey(sport.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wider transition-colors ${
              activeKey === sport.key
                ? 'bg-yellow-500 text-slate-900 shadow-[0_10px_20px_rgba(234,179,8,0.2)]'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
            }`}
          >
            <span className="text-base">{sport.emoji}</span>
            {sport.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex items-center justify-center min-h-[40vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center justify-center min-h-[40vh] text-center animate-in fade-in">
          <div className="bg-slate-900 border border-red-500/30 p-8 rounded-2xl max-w-md">
            <Activity size={48} className="text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-black text-white mb-2">Sin análisis disponible</h2>
            <p className="text-slate-400 mb-4">{error}</p>
            <button
              onClick={() => fetchAnalisis(activeSport)}
              className="px-6 py-2 bg-yellow-500 text-slate-900 font-bold rounded-lg hover:bg-yellow-400 transition-colors"
            >
              Reintentar
            </button>
          </div>
        </div>
      )}

      {!loading && !error && analisis && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="bg-slate-900/50 border border-slate-800 p-5 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <Calendar size={18} className="text-yellow-500" />
                <span className="text-slate-400 text-sm font-medium">Fecha Análisis</span>
              </div>
              <p className="text-white font-black text-lg">{formatDate(analisis.fecha || new Date())}</p>
            </div>

            <div className="bg-slate-900/50 border border-slate-800 p-5 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <Activity size={18} className="text-yellow-500" />
                <span className="text-slate-400 text-sm font-medium">Deporte</span>
              </div>
              <p className="text-white font-black text-lg">{activeSport.label}</p>
            </div>
          </div>

          {analisis.puntos_clave && analisis.puntos_clave.length > 0 && (
            <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-xl mb-8">
              <h2 className="text-lg font-black text-yellow-500 mb-2 uppercase tracking-wider">Puntos Clave</h2>
              <ul className="space-y-3">
                {analisis.puntos_clave.map((punto, i) => (
                  <li key={i} className="text-slate-300 leading-relaxed flex gap-2">
                    <span className="text-yellow-500 font-bold mt-0.5 shrink-0">◆</span>
                    <span>{punto}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden mb-8">
            <div className="p-4 border-b border-slate-800 bg-slate-950/50">
              <h2 className="text-xl font-black text-white flex items-center gap-2">
                <Target size={20} className="text-yellow-500" />
                Partidos del Día
              </h2>
            </div>

            <div className="divide-y divide-slate-800">
              {analisis.partidos?.map((partido, index) => {
                const hero = partido[activeSport.heroField];
                const market = partido.mercado;
                const lineValue = market ? market[activeSport.market.line] : null;
                const totalValue = market ? market[activeSport.market.total] : null;
                const spreadValue = market && activeSport.market.spread ? market[activeSport.market.spread] : null;

                return (
                  <div key={index} className="p-4 hover:bg-slate-800/30 transition-colors">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      <div className="flex items-center gap-4 min-w-[200px]">
                        <span className="text-2xl">{teamLogos[hero?.equipo] || activeSport.emoji}</span>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-black text-lg">{hero?.equipo}</span>
                            <span className="text-slate-500">vs</span>
                            <span className="text-white font-black text-lg">{hero?.oponente}</span>
                          </div>
                          <p className="text-slate-400 text-sm">vs {hero?.oponente}</p>
                        </div>
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-yellow-500 font-bold">{activeSport.heroLabel}:</span>
                          <span className="text-white font-medium">{hero?.nombre}</span>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {market && (
                            <>
                              {lineValue && (
                                <span className="text-xs bg-slate-800 text-yellow-400 px-2 py-1 rounded font-mono">
                                  {lineValue}
                                </span>
                              )}
                              {totalValue !== undefined && totalValue !== null && (
                                <span className="text-xs bg-slate-800 text-blue-400 px-2 py-1 rounded font-mono">
                                  {activeSport.market.totalLabel} {totalValue}
                                </span>
                              )}
                              {spreadValue && (
                                <span className="text-xs bg-slate-800 text-purple-400 px-2 py-1 rounded font-mono">
                                  {activeSport.market.spreadLabel}: {spreadValue}
                                </span>
                              )}
                              {market.tendencia_publico && (
                                <span className="text-xs bg-slate-800 text-slate-300 px-2 py-1 rounded">
                                  {market.tendencia_publico}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      <div className="min-w-[150px]">
                        <p className="text-slate-400 text-xs mb-1 uppercase tracking-wider">Probabilidad</p>
                        {getProbabilityBar(partido.probabilidad_victoria)}
                      </div>

                      <div className="min-w-[250px]">
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="flex items-start gap-1">
                            <TrendingUp size={14} className="text-green-400 mt-0.5" />
                            <span className="text-green-400 text-xs">{partido.factor_positivo}</span>
                          </div>
                          <div className="flex items-start gap-1">
                            <TrendingDown size={14} className="text-red-400 mt-0.5" />
                            <span className="text-red-400 text-xs">{partido.factor_negativo}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ─── PRONÓSTICOS ESPECIALES ─── */}
          <div className="mb-8">
            <h2 className="text-xl font-black text-white flex items-center gap-2 mb-6">
              <Star size={20} className="text-yellow-500" />
              Pronósticos Especiales
            </h2>

            {(() => {
              /* ─── LOG DE DEBUG (visible en consola del navegador) ─── */
              const keys = Object.keys(analisis);
              const hasPronosticos = keys.filter(k => k.toLowerCase().includes('pronostico') || k.toLowerCase().includes('recomend'));
              console.log(`[${activeSport.key}] Campos disponibles:`, keys.join(', '));
              console.log(`[${activeSport.key}] Posibles pronósticos:`, hasPronosticos.length ? hasPronosticos : '❌ NINGUNO');

              /* ─── DETECCIÓN AUTOMÁTICA del campo que contiene los pronósticos ─── */
              let ps;

              // 1. Buscar campo que contenga "pronostico" o "recomend" (insensible a mayúsculas)
              const campoPronostico = keys.find(k =>
                k.toLowerCase().includes('pronostico') || k.toLowerCase().includes('recomend')
              );
              if (campoPronostico) ps = analisis[campoPronostico];

              // 2. Si no encontró, probar campos directos
              if (!ps) ps = analisis?.pronosticos_especiales || analisis?.pronosticos_recomendados;

              // 3. Si sigue sin haber datos, mostrar placeholder
              if (!ps || typeof ps !== 'object') {
                return (
                  <div className="bg-slate-900/50 border border-dashed border-slate-700 p-8 rounded-2xl text-center">
                    <AlertTriangle size={32} className="text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-500 font-bold text-sm">
                      No hay pronósticos especiales disponibles para esta jornada
                    </p>
                    <p className="text-slate-600 text-xs mt-1">
                      (Revisá la consola del navegador para ver los campos disponibles)
                    </p>
                  </div>
                );
              }

              /* ─── CONFIGURACIÓN DE CARDS ─── */
              const cards = [
                { keys: ['fijo_del_dia', 'el_fijo_del_dia', 'fijo'],          titulo: 'El Fijo del Día',       icon: Star,         color: 'yellow' },
                { keys: ['hembra_del_dia', 'hembra'],                          titulo: 'Hembra del Día',         icon: Zap,          color: 'purple' },
                { keys: ['alta_mas_probable', 'la_alta_mas_probable'],         titulo: 'La Alta Más Probable',  icon: TrendingUp,   color: 'green' },
                { keys: ['baja_mas_probable', 'la_baja_mas_probable'],         titulo: 'La Baja Más Probable',   icon: AlertTriangle, color: 'blue' }
              ];

              const gradMap = {
                yellow: { from: 'from-yellow-500/20', to: 'to-yellow-600/10', border: 'border-yellow-500/30', icon: 'text-yellow-500', label: 'text-yellow-500' },
                purple: { from: 'from-purple-500/20', to: 'to-purple-600/10', border: 'border-purple-500/30', icon: 'text-purple-400', label: 'text-purple-400' },
                green:  { from: 'from-green-500/20',  to: 'to-green-600/10',  border: 'border-green-500/30',  icon: 'text-green-400',  label: 'text-green-400' },
                blue:   { from: 'from-blue-500/20',   to: 'to-blue-600/10',   border: 'border-blue-500/30',   icon: 'text-blue-400',   label: 'text-blue-400' }
              };

              const getCardData = (card) => {
                for (const k of card.keys) {
                  if (ps[k] && typeof ps[k] === 'object') return ps[k];
                }
                return null;
              };

              const cardsConDatos = cards.map(c => ({ ...c, data: getCardData(c) }));

              /* ─── SI NINGUNA CARD TIENE DATOS, mostrar placeholder ─── */
              if (!cardsConDatos.some(c => c.data)) {
                const keysEncontradas = Object.keys(ps);
                return (
                  <div className="bg-slate-900/50 border border-dashed border-orange-700/50 p-8 rounded-2xl text-center">
                    <AlertTriangle size={32} className="text-orange-500 mx-auto mb-3" />
                    <p className="text-slate-400 font-bold text-sm mb-1">
                      Se encontró el campo <code className="text-yellow-500 bg-slate-800 px-1 rounded">{campoPronostico || 'pronosticos'}</code>
                    </p>
                    <p className="text-slate-500 text-xs mb-3">
                      pero no coincidió con los nombres de sub-campos esperados.
                      <br />Sub-campos disponibles: <span className="text-blue-400">{keysEncontradas.join(', ') || 'ninguno'}</span>
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {keysEncontradas.map(k => (
                        <span key={k} className="text-[10px] bg-slate-800 text-slate-300 px-2 py-1 rounded font-mono">{k}</span>
                      ))}
                    </div>
                  </div>
                );
              }

              /* ─── RENDER: cards con datos ─── */
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {cardsConDatos.map((card, i) => {
                    if (!card.data) return null;
                    const g = gradMap[card.color];
                    const Icon = card.icon;
                    const mainText = getCardMainText(card.data);
                    const subText = getCardSubText(card.data);
                    const body = getCardBody(card.data);
                    return (
                      <div key={i} className={`bg-gradient-to-br ${g.from} ${g.to} border ${g.border} p-5 rounded-2xl flex flex-col`}>
                        <div className="flex items-center gap-2 mb-3">
                          <Icon size={18} className={g.icon} />
                          <span className={`${g.label} font-black text-sm uppercase tracking-wider`}>{card.titulo}</span>
                        </div>
                        {mainText && <p className="text-white font-black text-lg mb-1 leading-tight">{mainText}</p>}
                        {subText && (
                          <p className="text-slate-400 text-xs mb-2 font-semibold">{subText}</p>
                        )}
                        {body && <p className="text-slate-500 text-sm mt-auto leading-relaxed">{body}</p>}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {analisis.origen_datos && (
            <div className="p-4 bg-slate-900/30 border border-slate-800 rounded-xl text-center">
              <p className="text-slate-500 text-sm">
                Fuente:{' '}
                <a
                  href={analisis.origen_datos}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-yellow-500 hover:text-yellow-400 underline break-all"
                >
                  {analisis.origen_datos}
                </a>
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
